// Short-link helpers. The public short-link host (the micro-site) is a different
// origin from this app, configured via VUE_APP_SHORT_BASE_URL (e.g. https://myurl.to,
// or http://localhost:8787 in dev). Falls back to this app's origin if unset.
import { config } from '@/runtimeConfig';

export function shortBase() {
  const base = (config.shortBaseUrl || '').trim();
  if (base) return base.replace(/\/+$/, '');
  return (typeof window !== 'undefined' ? window.location.origin : '');
}

// Full, clickable/copyable short URL, e.g. https://myurl.to/abc123
export function shortUrl(slug) {
  return `${shortBase()}/${slug}`;
}

// Display form without the protocol, e.g. myurl.to/abc123
export function shortDisplay(slug) {
  return `${shortBase().replace(/^https?:\/\//, '')}/${slug}`;
}

// ---- Canonical paste URLs ----
// Pastes live on this app's origin (like /files/:id), NOT on the short host:
// a short link to a paste is a separate opt-in that 302s here.

export function pasteUrl(pasteId) {
  return `${window.location.origin}/p/${pasteId}`;
}

// Display form without the protocol, e.g. linker.to/p/AbC123xyz0
export function pasteDisplay(pasteId) {
  return `${window.location.host}/p/${pasteId}`;
}

// Raw text endpoint (backend, text/plain). Only meaningful for pastes without
// a password or burn-after-read — the backend 403s gated pastes.
export function pasteRawUrl(pasteId) {
  const base = (config.backendBaseUrl || '').replace(/\/+$/, '');
  return `${base}/pastes/${pasteId}/raw`;
}

// Slug rules must match the backend (app/api_v1/links/schemas.py SLUG_PATTERN).
const SLUG_RE = /^[A-Za-z0-9_-]{1,64}$/;
const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;
// Registrable hostname: labels + a >=2-letter TLD. Mirrors the backend host check.
const HOST_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))*\.[a-z]{2,}$/i;

export function isValidSlug(slug) {
  return SLUG_RE.test(slug);
}

// Normalize user input into a valid http(s) URL, or explain why it isn't one.
// Adds https:// when no scheme is present, but only accepts a real domain/IP —
// junk like `{"not-real":true}` is rejected rather than silently prefixed.
export function normalizeUrl(input) {
  const raw = (input || '').trim();
  if (!raw) return { ok: false, error: 'Enter a URL to shorten' };
  const candidate = /^https?:\/\//i.test(raw) ? raw : 'https://' + raw;
  let u;
  try {
    u = new URL(candidate);
  } catch (e) {
    return { ok: false, error: "That doesn't look like a valid URL" };
  }
  if (u.protocol !== 'http:' && u.protocol !== 'https:') {
    return { ok: false, error: 'Only http(s) links can be shortened' };
  }
  if (!IPV4_RE.test(u.hostname) && !HOST_RE.test(u.hostname)) {
    return { ok: false, error: 'Enter a valid domain, e.g. example.com' };
  }
  return { ok: true, url: u.href };
}
